"use client";

import { Button, buttonVariants } from "@/components/atoms/buttons";
import { TableCell, TableRow } from "@/components/ui/table";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn, convertDate, formatPrice } from "@/lib/utils";
import Link from "next/link";
import { StoreOrder } from "@medusajs/types";
import { Locales } from "@/lib/i18n";
import { useCart } from "@/hooks/useCart";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { toast } from "@/components/atoms/toast";
import { Loader2 } from "lucide-react";
import { Graphics } from "@/components/graphics";
import { useProducts } from "@/hooks/useProduct";
import { useTransition } from "react";
import { downloadInvoiceHandler } from "@/lib/invoice/download-invoice";

type OrderTableBodyProps = {
  order: StoreOrder;
  index: number;
  locale: Locales;
};

const OrderTableBody = ({ order, locale, index }: OrderTableBodyProps) => {
  const { cart } = useCart();
  const [isPending, startTransition] = useTransition();

  const { data: medusaProducts, isLoading } = useProducts(
    locale,
    order?.items?.map((item) => item.product_id || "") || []
  );

  const { addToCart, isLoading: isAddingAllToCart } = useAddToCart({
    onError: (error) => {
      console.error("Failed to add item to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });
  const { removeItem } = useRemoveCartItem();
  const { updateQuantity } = useUpdateCartItem();
  const productsArray = Object.values(medusaProducts || {});

  const orderItemVariantIds =
    order?.items
      ?.filter((item) => {
        const product = medusaProducts?.[item.product_id || ""];
        return product && product.status !== "draft";
      })
      .map((item) => item.variant_id)
      .filter((id): id is string => !!id) ?? [];

  const handleAddAll = async () => {
    try {
      const ops = orderItemVariantIds.map(async (variantId) => {
        const orderItem = order?.items?.find((item) => item.variant_id === variantId);
        const orderQty = orderItem?.quantity || 1;
        const productId = orderItem?.product_id;

        const sameVariantInCart = cart?.items?.find((i) => i.variant_id === variantId);
        const differentVariantInCart = cart?.items?.find(
          (i) => i.product_id === productId && i.variant_id !== variantId
        );

        if (sameVariantInCart) {
          // Same variant already in cart — merge quantities
          updateQuantity(sameVariantInCart.id, sameVariantInCart.quantity + orderQty);
        } else if (differentVariantInCart) {
          // Different variant of same product in cart — swap it out
          removeItem(differentVariantInCart.id);
          addToCart(variantId, orderQty);
        } else {
          addToCart(variantId, orderQty);
        }
      });

      await Promise.all(ops);
      toast.success({ title: __("All products added to cart!", locale) });
    } catch (err) {
      console.error("Error adding all items to cart:", err);
      toast.error({ title: __("Failed to add some products to cart", locale) });
    }
  };

  const validVariantIds = orderItemVariantIds.filter(Boolean);

  const isProductsInCart =
    validVariantIds.length > 0 && validVariantIds.every((id) => cart?.items?.some((item) => item.variant_id === id));

  const isAllProductsOutOfStock = productsArray.every((product) => product.status === "draft");

  return (
    <TableRow key={order.id} className="border-primary-light/30">
      <TableCell className="text-dark-primary px-4 py-6 font-normal">#{index}</TableCell>
      {/* <TableCell className="text-dark-primary px-4 py-6 font-normal">{order.ref}</TableCell> */}
      <TableCell className="text-dark-primary px-4 py-6 font-normal capitalize">
        {order?.created_at ? convertDate(order?.created_at, "dd MMMM yyyy", locale) : "-"}
      </TableCell>
      <TableCell className="text-dark-primary px-4 py-6 font-normal">{order?.items?.length}</TableCell>
      <TableCell className="text-dark-primary px-4 py-6 font-normal">{formatPrice(order?.total)}</TableCell>

      <TableCell className="px-4 py-2 text-right">
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={() => startTransition(() => downloadInvoiceHandler(order.id, locale))}
            disabled={isPending}
            className="focus:outline-dark-primary inline-flex cursor-pointer items-center gap-2 p-1 transition-all disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? <Loader2 className="animate-spin" /> : <Graphics.pdfIcon />}
          </button>
          <Link
            href={`${getPermalink(locale, "account", "orders")}/${order.id}`}
            className={cn(
              buttonVariants({ variant: "secondary", size: "icon" }),
              "border-primary-light h-11 rounded-md px-4 py-2 font-normal"
            )}
          >
            {__("View", locale)}
          </Link>
          {isLoading ? (
            <div className="shimmer h-10 min-w-[106px] rounded-sm" />
          ) : isProductsInCart ? (
            <Link
              className={cn(buttonVariants({ size: "icon" }), "min-w-[106px] rounded-md")}
              href={getPermalink(locale, "cart")}
            >
              {__("View Cart", locale)}
            </Link>
          ) : (
            <Button
              onClick={handleAddAll}
              disabled={isAddingAllToCart || isLoading || isAllProductsOutOfStock}
              variant="secondary"
              size="icon"
              className={cn(
                "border-pink-light h-11 min-w-[106px] rounded-md px-4 py-2 font-normal",
                locale === "nl" && "min-w-[168px]"
              )}
            >
              {isAllProductsOutOfStock ? (
                __("Unavailable", locale)
              ) : isAddingAllToCart ? (
                <Loader2 className="animate-spin" />
              ) : (
                __("Duplicate", locale)
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default OrderTableBody;
